import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Grid, Box } from '@mui/material';
import { keyframes } from '@mui/system';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

/**
 * A toolbar component for displaying component settings on component hover.
 */
const ComponentSettingsToolbar = ({
  selected,
  actions,
  classes,
  data,
  enableWorkflowPath = false,
  canvasLocked = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const timeoutRef = useRef(null);
  const fadeOutTimeoutRef = useRef(null);

  useEffect(() => {
    if (selected && !(data.fromWorkflowPath && !enableWorkflowPath) && !canvasLocked) {
      setHasInteracted(true);
      setIsVisible(true);
      setIsFadingOut(false);
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
    } else if (!selected && hasInteracted) {
      // Only start fade out timer if user has interacted (prevents initial flash)
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setIsFadingOut(true);
        // Remove from DOM after animation completes
        fadeOutTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          setIsFadingOut(false);
        }, 300); // Match animation duration
      }, 250); // 0.25 second delay
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (fadeOutTimeoutRef.current) clearTimeout(fadeOutTimeoutRef.current);
    };
  }, [selected, data.fromWorkflowPath, enableWorkflowPath, canvasLocked, hasInteracted]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (fadeOutTimeoutRef.current) {
      clearTimeout(fadeOutTimeoutRef.current);
      fadeOutTimeoutRef.current = null;
      setIsFadingOut(false);
    }
  };

  const handleMouseLeave = () => {
    if (selected) return; // Don't hide if still selected
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsFadingOut(true);
      // Remove from DOM after animation completes
      fadeOutTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsFadingOut(false);
      }, 300); // Match animation duration
    }, 250); // 0.25 second delay
  };

  if (!isVisible && !isFadingOut) return null;
  if ((data.fromWorkflowPath && !enableWorkflowPath) || canvasLocked) return null;

  return (
    <Box
      className={`${classes.nodeInfoBackground} nodrag nopan`}
      sx={{
        animation: isFadingOut ? `${fadeOut} 0.3s ease-out forwards` : `${fadeIn} 0.3s ease-out`,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={classes.settings}>
        <Grid container>
          <Grid item xs={12} className={classes.centerGrid}>
            {actions.map((action, index) => (
              <Tooltip key={index} title={action.title}>
                <div className={classes.actionButton} onClick={action.onClick}>
                  {React.cloneElement(action.icon, {
                    style: { width: 16, height: 16, color: 'inherit' },
                  })}
                  {action.text && <span className={classes.actionText}>{action.text}</span>}
                </div>
              </Tooltip>
            ))}
          </Grid>
        </Grid>
      </div>
    </Box>
  );
};

ComponentSettingsToolbar.propTypes = {
  selected: PropTypes.bool.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      icon: PropTypes.node.isRequired,
      text: PropTypes.string,
    })
  ).isRequired,
  classes: PropTypes.shape({
    nodeInfoBackground: PropTypes.string.isRequired,
    settings: PropTypes.string.isRequired,
    centerGrid: PropTypes.string.isRequired,
    actionButton: PropTypes.string.isRequired,
    actionText: PropTypes.string.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    fromWorkflowPath: PropTypes.bool,
  }).isRequired,
  enableWorkflowPath: PropTypes.bool,
  canvasLocked: PropTypes.bool,
};

export default ComponentSettingsToolbar;
